import {
  ChevronDownIcon,
  DownloadIcon,
  ExternalLinkIcon,
} from '@heroicons/react/outline';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  downloadSupportMedia,
  getAdministrationSupportContent,
} from '../../api/support';
import IntroSctTitle from '../../components/IntroSctTitle';
import Spinner from '../../components/Layout/Loading/Spinner';
import TitlePage from '../../components/TitlePage';
import { useAuth } from '../../contexts/Auth';
import { SupportFile } from '../../interfaces/support';

const Supporto = () => {
  const [{ token }] = useAuth();
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);

  const supportQuery = useQuery(
    ['support-content', 'amministrazione'],
    () => getAdministrationSupportContent(token),
    {
      enabled: Boolean(token),
    }
  );

  const content = supportQuery.data?.data;
  const groupedFiles = useMemo(
    () =>
      (content?.files || []).reduce<Record<string, SupportFile[]>>(
        (groups, file) => {
          const sectionName = file.sectionName || 'Tutorial';
          groups[sectionName] = [...(groups[sectionName] || []), file];
          return groups;
        },
        {}
      ),
    [content?.files]
  );

  return (
    <div>
      <TitlePage title='Supporto' />
      <IntroSctTitle>
        <span className='font-medium'>Supporto</span>
      </IntroSctTitle>

      <main className='px-4 py-10 lg:px-10 lg:py-14'>
        <div className='mx-auto max-w-5xl space-y-14 text-gray-700'>
          {supportQuery.isLoading && (
            <div className='flex justify-center py-20'>
              <Spinner />
            </div>
          )}

          {supportQuery.isError && (
            <div
              className='rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800'
              role='alert'
            >
              Non è stato possibile caricare i contenuti di supporto. Riprova
              più tardi.
            </div>
          )}

          {content && (
            <>
              <section aria-labelledby='support-faq-title'>
                <h2
                  id='support-faq-title'
                  className='mb-6 font-IBM text-2xl font-light text-gray-800 md:text-3xl'
                >
                  Domande frequenti
                </h2>

                {content.faqs.length > 0 ? (
                  <div className='divide-y divide-gray-200 border-y border-gray-200'>
                    {content.faqs.map((faq) => {
                      const isOpen = openFaqId === faq.id;
                      const answerId = `support-faq-answer-${faq.id}`;

                      return (
                        <div key={faq.id}>
                          <button
                            type='button'
                            className='flex w-full items-center justify-between gap-4 py-5 text-left text-gray-800 hover:text-primary'
                            aria-expanded={isOpen}
                            aria-controls={answerId}
                            onClick={() =>
                              setOpenFaqId(isOpen ? null : faq.id)
                            }
                          >
                            <span className='text-lg font-medium'>
                              {faq.question}
                            </span>
                            <ChevronDownIcon
                              className={`h-5 w-5 shrink-0 transition-transform ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                              aria-hidden='true'
                            />
                          </button>

                          {isOpen && (
                            <div
                              id={answerId}
                              className='support-rich-text pb-6 pr-8 text-gray-600'
                              dangerouslySetInnerHTML={{
                                __html: faq.answer,
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className='text-gray-500'>
                    Nessuna domanda frequente disponibile al momento.
                  </p>
                )}
              </section>

              <section aria-labelledby='support-tutorial-title'>
                <h2
                  id='support-tutorial-title'
                  className='mb-6 font-IBM text-2xl font-light text-gray-800 md:text-3xl'
                >
                  Tutorial
                </h2>

                {Object.keys(groupedFiles).length > 0 ? (
                  <div className='space-y-10'>
                    {Object.entries(groupedFiles).map(
                      ([sectionName, files]) => (
                        <div key={sectionName}>
                          <h3 className='mb-3 border-b border-gray-300 pb-2 text-xl font-medium text-gray-800'>
                            {sectionName}
                          </h3>
                          <div className='divide-y divide-gray-200'>
                            {files.map((file) => (
                              <div
                                key={file.id}
                                className='flex flex-col justify-between gap-4 py-5 sm:flex-row sm:items-center'
                              >
                                <div>
                                  <h4 className='font-medium uppercase text-gray-700'>
                                    {file.title}
                                  </h4>
                                  {file.caption && (
                                    <p className='mt-1 text-sm italic text-gray-500'>
                                      {file.caption}
                                    </p>
                                  )}
                                </div>

                                {file.media ? (
                                  <button
                                    type='button'
                                    onClick={() =>
                                      file.media &&
                                      downloadSupportMedia(file.media)
                                    }
                                    className='inline-flex shrink-0 items-center gap-2 self-start text-sm font-medium text-primary hover:opacity-70 sm:self-auto'
                                  >
                                    <DownloadIcon className='h-5 w-5' />
                                    Scarica
                                  </button>
                                ) : file.url ? (
                                  <a
                                    href={file.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='inline-flex shrink-0 items-center gap-2 self-start text-sm font-medium text-primary hover:opacity-70 sm:self-auto'
                                  >
                                    <ExternalLinkIcon className='h-5 w-5' />
                                    Apri tutorial
                                  </a>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <p className='text-gray-500'>
                    Nessun tutorial disponibile al momento.
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Supporto;
